import { Action, Dispatch } from 'redux';
import { AppState, history } from '..';
import { propertyManagementService } from '@services/PropertyManagementService';
import { PropertyManagementOfficeViewModel } from '@models/PropertyManagementOffice';
import { toast } from 'react-toastify';
import { ToastComponent, ToastType, ToastOptions } from '@common/components';
import * as React from 'react';
import { actions } from 'react-redux-form';
import { PageViewModel } from '@models/common.models';
import { PropertyManagerSearchViewModel } from '@models/PropertyManager';
import { RouteDefinitions } from '@common/route-definitions';
import { getRoute } from '../common/route-definitions';
import FormNames from '@constants/FormNames.enum';

export enum PropertyManagementActionType {
  GetPropertyManagementOfficeDetailsStart = 'GetPropertyManagementOfficeDetailsStart',
  GetPropertyManagementOfficeDetailsSuccess = 'GetPropertyManagementOfficeDetailsSuccess',
  GetPropertyManagementOfficeDetailsFailure = 'GetPropertyManagementOfficeDetailsFailure',

  SavePropertyManagementOfficeStart = 'SavePropertyManagementOfficeStart',
  SavePropertyManagementOfficeSuccess = 'SavePropertyManagementOfficeSuccess',
  SavePropertyManagementOfficeFailure = 'SavePropertyManagementOfficeFailure',

  SearchPropertyManagmentOfficeInit = 'SearchPropertyManagmentOfficeInit',
  SearchPropertyManagmentOfficeSuccess = 'SearchPropertyManagmentOfficeSuccess',
  SearchPropertyManagmentOfficeFailure = 'SearchPropertyManagmentOfficeFailure',

  GetPropertyManagersStart = 'GetPropertyManagersStart',
  GetPropertyManagersSuccess = 'GetPropertyManagersSuccess',
  GetPropertyManagersFailure = 'GetPropertyManagersFailure',

  ClearPropertyManagementOffice = 'ClearPropertyManagementOffice'
}

// This is defining the action definition for certain actions on the page.
// There may be multiple types of these on a given page.
// All properties should be readonly.

/********** Get **********/
export interface GetPropertyManagementDetailsStartAction extends Action {
  readonly type: PropertyManagementActionType.GetPropertyManagementOfficeDetailsStart;
  readonly id: number;
}

export interface GetPropertyManagementOfficeDetailsSuccessAction extends Action {
  readonly type: PropertyManagementActionType.GetPropertyManagementOfficeDetailsSuccess;
  readonly propertyManagementVM: PropertyManagementOfficeViewModel;
}

export interface GetPropertyManagementOfficeDetailsFailureAction extends Action {
  readonly type: PropertyManagementActionType.GetPropertyManagementOfficeDetailsFailure;
  readonly message: string;
}

export interface SavePropertyManagementOfficeStartAction extends Action {
  readonly type: PropertyManagementActionType.SavePropertyManagementOfficeStart;
}

export interface SavePropertyManagementOfficeSuccessAction extends Action {
  readonly type: PropertyManagementActionType.SavePropertyManagementOfficeSuccess;
  readonly id: number;
}

export interface SavePropertyManagementOfficeFailureAction extends Action {
  readonly type: PropertyManagementActionType.SavePropertyManagementOfficeFailure;
  readonly message: string;
}

export interface PropertyManagementOfficeSearchInitAction extends Action {
  readonly type: PropertyManagementActionType.SearchPropertyManagmentOfficeInit;
  readonly query: string | null;
}

export interface PropertyManagementOfficeSearchSuccessAction extends Action {
  readonly type: PropertyManagementActionType.SearchPropertyManagmentOfficeSuccess;
  readonly pageNumber: number;
  readonly pageSize: number;
  readonly orderBy: string;
  readonly result: PageViewModel<PropertyManagementOfficeViewModel>;
}

export interface PropertyManagementOfficeSearchFailureAction extends Action {
  readonly type: PropertyManagementActionType.SearchPropertyManagmentOfficeFailure;
  readonly errorMessage: string;
}

export interface GetPropertyManagersStartAction extends Action {
  readonly type: PropertyManagementActionType.GetPropertyManagersStart;
}

export interface GetPropertyManagersSuccessAction extends Action {
  readonly type: PropertyManagementActionType.GetPropertyManagersSuccess;
  readonly propertyManagers: Array<PropertyManagerSearchViewModel>;
}

export interface GetPropertyManagersFailureAction extends Action {
  readonly type: PropertyManagementActionType.GetPropertyManagersFailure;
  readonly message: string;
}

export interface ClearPropertyManagementOfficeAction extends Action {
  readonly type: PropertyManagementActionType.ClearPropertyManagementOffice;
}

export function clearPropertyManagementOffice(): ClearPropertyManagementOfficeAction {
  return {
    type: PropertyManagementActionType.ClearPropertyManagementOffice
  };
}
export function getPropertyManagementOfficeDetailsStart(id: number): GetPropertyManagementDetailsStartAction {
  return {
    type: PropertyManagementActionType.GetPropertyManagementOfficeDetailsStart,
    id: id
  };
}

export function getPropertyManagementOfficeDetailsSuccess(
  office: PropertyManagementOfficeViewModel
): GetPropertyManagementOfficeDetailsSuccessAction {
  return {
    type: PropertyManagementActionType.GetPropertyManagementOfficeDetailsSuccess,
    propertyManagementVM: office
  };
}

export function getPropertyManagementOfficeDetailsFailure(
  err: string
): GetPropertyManagementOfficeDetailsFailureAction {
  toast.error(
    <ToastComponent type={ToastType.Error} message={'Network error, unable to load office details' + err} />,
    ToastOptions.Error
  );
  return {
    type: PropertyManagementActionType.GetPropertyManagementOfficeDetailsFailure,
    message: err
  };
}

export function getPropertyManagementOfficeDetails(id?: number) {
  return async (dispatch: Dispatch<AppState>) => {
    dispatch(actions.reset('propertyManagementOfficeForm'));
    if (id) {
      dispatch(getPropertyManagementOfficeDetailsStart(id));
      try {
        const office = await propertyManagementService.getOfficeDetails(id);
        dispatch(getPropertyManagementOfficeDetailsSuccess(office));
        dispatch(actions.change(FormNames.PropertyManagementOfficeForm, office));
      } catch (err) {
        dispatch(getPropertyManagementOfficeDetailsFailure(err));
      }
    }
  };
}

export const getPropertyManagersById = (id: number) => {
  return async (dispatch: Dispatch<AppState>) => {
    dispatch(getPropertyManagersStart());
    try {
      var propertyManagers = await propertyManagementService.getPropertyManagers(id);
      dispatch(getPropertyManagersSuccess(propertyManagers));
    } catch (e) {
      dispatch(getPropertyManagersFailure(e.toString()));
    }
  };
};

export function getPropertyManagersStart(): GetPropertyManagersStartAction {
  return {
    type: PropertyManagementActionType.GetPropertyManagersStart
  };
}

export function getPropertyManagersSuccess(
  propertyManagers: Array<PropertyManagerSearchViewModel>
): GetPropertyManagersSuccessAction {
  return {
    type: PropertyManagementActionType.GetPropertyManagersSuccess,
    propertyManagers: propertyManagers
  };
}

export function getPropertyManagersFailure(error: string): GetPropertyManagersFailureAction {
  return {
    type: PropertyManagementActionType.GetPropertyManagersFailure,
    message: error
  };
}
export function savePropertyManagementOfficeStart(): SavePropertyManagementOfficeStartAction {
  return {
    type: PropertyManagementActionType.SavePropertyManagementOfficeStart
  };
}

export function savePropertyManagementOfficeSuccess(id: number): SavePropertyManagementOfficeSuccessAction {
  toast.success(
    <ToastComponent type={ToastType.Success} message={'Successfully Saved Office'} />,
    ToastOptions.Success
  );
  history.push(getRoute(RouteDefinitions.propertymanagementoffice.Details, { id: id }));
  return {
    type: PropertyManagementActionType.SavePropertyManagementOfficeSuccess,
    id: id
  };
}

export function savePropertyManagementOfficeFailure(errorMessage: string): SavePropertyManagementOfficeFailureAction {
  toast.error(
    <ToastComponent type={ToastType.Error} message={'Unable to save Office. Error:' + errorMessage} />,
    ToastOptions.Error
  );
  return {
    type: PropertyManagementActionType.SavePropertyManagementOfficeFailure,
    message: errorMessage
  };
}

export function savePropertyManagementOffice(office: PropertyManagementOfficeViewModel) {
  return async (dispatch: Dispatch<AppState>) => {
    dispatch(savePropertyManagementOfficeStart());
    try {
      const id = await propertyManagementService.saveOffice(office);
      dispatch(savePropertyManagementOfficeSuccess(id));
    } catch (err) {
      dispatch(savePropertyManagementOfficeFailure(err));
    }
  };
}

/************ Search Offices **************/

export function propertyManagementOfficeSeachInit(query: string | null): PropertyManagementOfficeSearchInitAction {
  return {
    type: PropertyManagementActionType.SearchPropertyManagmentOfficeInit,
    query: query
  };
}

export function propertyManagementOfficeSearchSuccess(
  pageNumber: number,
  pageSize: number,
  orderBy: string,
  result: PageViewModel<PropertyManagementOfficeViewModel>
): PropertyManagementOfficeSearchSuccessAction {
  return {
    pageNumber: pageNumber,
    pageSize: pageSize,
    orderBy: orderBy,
    result: result,
    type: PropertyManagementActionType.SearchPropertyManagmentOfficeSuccess
  };
}

export function propertyManagementOfficeSearchFailure(
  errorMessage: string
): PropertyManagementOfficeSearchFailureAction {
  toast.error(
    <ToastComponent type={ToastType.Error} message={'Network error, Unable to search offices. ' + errorMessage} />,
    ToastOptions.Error
  );

  return {
    type: PropertyManagementActionType.SearchPropertyManagmentOfficeFailure,
    errorMessage: 'Error searching offices'
  };
}

export function searchPropertyManagementOffices(
  query: string | null,
  pageSize: number,
  pageNumber: number,
  orderBy: string,
  orderDirection: string
) {
  return async (dispatch: Dispatch<AppState>) => {
    dispatch(propertyManagementOfficeSeachInit(query));
    try {
      let result: PageViewModel<PropertyManagementOfficeViewModel> = await propertyManagementService.searchOffices(
        query,
        null,
        pageSize,
        pageNumber,
        orderBy,
        orderDirection
      );
      dispatch(propertyManagementOfficeSearchSuccess(pageNumber, pageSize, orderBy, result));
    } catch (e) {
      dispatch(propertyManagementOfficeSearchFailure(e.toString()));
    }
    return;
  };
}

/*
export const escrowOfficeSearch = (query: string | null, pageSize: number, pageNumber: number, orderBy: string) => {
    return async (dispatch: Dispatch<OfficeSearchViewModel>) => {
        dispatch(escrowOfficeSearchInit(query));
        try {
            let result: PageViewModel<OfficeSearchViewModel> = await escrowService.searchOfficesFull(query, pageSize, pageNumber, orderBy);
            dispatch(escrowOfficeSearchSuccess(pageNumber, pageSize, orderBy, result));
        } catch (e) {
            dispatch(escrowOfficeSearchFailure(e.toString()));
        }
    };
};
*/



// WEBPACK FOOTER //
// ./src/property-management/property-management.actions.tsx